/**
 * The reach HUD view (plan R15): a DOM overlay under `#reach-hud`, appended to `ctx.hudRoot` next to M1's HUD and
 * built once at init. Like M1's hud.ts, per frame it writes only `transform`, `opacity`, `textContent` and `data-*`
 * attributes on cached elements, never reads layout, and diffs every value against the last one written (quantised
 * to what is visible), so a steady HUD writes nothing. Text readouts are keyed (format.ts): a string is built only
 * when the text it would show changes (D-014).
 */
import { durationKey, distanceKey, formatDistance, formatDuration, formatNavDistance, formatSpeed, navDistanceKey, speedKey } from './format'
import { FAR_RELAY, type ReachHudRules, type ReachHudState } from './model'
import { createLabelDeclutter, declutterLabels, LABEL_FULL, LABEL_HIDDEN, PART_LABELS, reserveLabelRect, resetLabelDeclutter, type LabelDeclutter } from './labels'
import { loadReachHudConfig, type HudLabelMode, type ReachHudConfig } from './config'
import { toastOpacity } from './toasts'
import { PART_IDS } from '../../reach/types'

/** The subset of `CSSStyleDeclaration` the view writes. */
export interface ReachHudStyle {
  transform: string
  opacity: string
  strokeDashoffset: string
}

/** The subset of an element the view writes (tests pass fakes that count writes). */
export interface ReachHudNode {
  textContent: string | null
  readonly style: ReachHudStyle
  setAttribute(name: string, value: string): void
}

/** A world marker: its group (placed by transform), the arrow head (rotated off screen), name and distance. */
export interface MarkerNodes {
  root: ReachHudNode
  head: ReachHudNode
  name: ReachHudNode
  dist: ReachHudNode
}

/** A far nav marker (D-249): its group (placed by transform, kind and edge as data-*), the edge arrow and the distance. */
export interface FarNodes {
  root: ReachHudNode
  arrow: ReachHudNode
  dist: ReachHudNode
}

export interface LabelNodes {
  root: ReachHudNode
  name: ReachHudNode
  dist: ReachHudNode
}

/** Every element the view writes, created once by `mountReachHud`. */
export interface ReachHudElements {
  root: ReachHudNode
  nav: MarkerNodes
  route: MarkerNodes
  labels: LabelNodes[]
  far: FarNodes[]
  farLayer: ReachHudNode
  cruise: ReachHudNode
  cruiseState: ReachHudNode
  cruiseCharge: ReachHudNode
  cruiseSpeed: ReachHudNode
  cruiseEta: ReachHudNode
  /** "ETA ⟨time⟩": shown in cruise only while there is a nav target (H1). */
  cruiseEtaWrap: ReachHudNode
  cruiseThrottle: ReachHudNode
  cruiseThrottleFill: ReachHudNode
  cruiseRing: ReachHudNode
  escape: MarkerNodes
  intd: ReachHudNode
  intdEscape: ReachHudNode
  intdPull: ReachHudNode
  intdTime: ReachHudNode
  relay: ReachHudNode
  relayDest: ReachHudNode
  relayStatus: ReachHudNode
  relayDist: ReachHudNode
  relayAlign: ReachHudNode
  relaySpeed: ReachHudNode
  relayRepair: ReachHudNode
  relayParts: ReachHudNode[]
  scan: ReachHudNode
  scanArc: ReachHudNode
  scanPing: ReachHudNode
  scanBlocked: ReachHudNode
  /** The status slot above the cruise panel (H3): the scan state and its refusal, never at the screen centre. */
  status: ReachHudNode
  relink: ReachHudNode
  relinkHull: ReachHudNode
  relinkTime: ReachHudNode
  relinkWave: ReachHudNode
  prompt: ReachHudNode
  promptKey: ReachHudNode
  promptText: ReachHudNode
  promptBlocked: ReachHudNode
  cruiseHint: ReachHudNode
  banner: ReachHudNode
  bannerName: ReachHudNode
  bannerSub: ReachHudNode
  toasts: ReachHudNode[]
}

/** Scan ring radius in its 100 × 100 SVG, and its circumference (the dash length). */
const SCAN_R = 40
const SCAN_C = 2 * Math.PI * SCAN_R
const RAD_TO_DEG = 180 / Math.PI

/** One diffed write target: a node plus the last values written to one of its properties. */
interface Channel {
  node: ReachHudNode
  a: number
  b: number
  c: number
  s: string
}

function channel(node: ReachHudNode): Channel {
  return { node, a: NaN, b: NaN, c: NaN, s: '\u0000' }
}

interface MarkerChannels {
  place: Channel
  head: Channel
  name: Channel
  dist: Channel
  on: Channel
  edge: Channel
}

function markerChannels(m: MarkerNodes): MarkerChannels {
  return { place: channel(m.root), head: channel(m.head), name: channel(m.name), dist: channel(m.dist), on: channel(m.root), edge: channel(m.root) }
}

interface LabelChannels {
  place: Channel
  name: Channel
  dist: Channel
  on: Channel
  kind: Channel
  /** data-text: 1 = name and distance on the plate, 0 = icon only (decluttered). */
  text: Channel
  fade: Channel
}

/** What the label declutter needs from the frame: the viewport (CSS px) and whether a combat target is locked. */
export interface LabelFrame {
  width: number
  height: number
  locked: boolean
}

interface FarChannels {
  place: Channel
  arrow: Channel
  dist: Channel
  on: Channel
  kind: Channel
  edge: Channel
}

interface ToastChannels {
  text: Channel
  on: Channel
  tone: Channel
  opacity: Channel
  version: number
}

export interface ReachHudView {
  el: ReachHudElements
  on: Channel
  nav: MarkerChannels
  navRoute: Channel
  route: MarkerChannels
  labels: LabelChannels[]
  /** Declutter scratch (H2) and the full-label cap of the HUD labels setting. */
  declutter: LabelDeclutter
  labelCap: number
  frame: LabelFrame
  far: FarChannels[]
  farLayer: Channel
  cruisePanel: Channel
  cruiseState: Channel
  cruiseCharge: Channel
  cruiseSpeed: Channel
  cruiseEta: Channel
  cruiseEtaOn: Channel
  cruiseThrottle: Channel
  cruiseThrottleFill: Channel
  cruiseRing: Channel
  escape: MarkerChannels
  intdOn: Channel
  intdEscape: Channel
  intdPull: Channel
  intdTime: Channel
  relayOn: Channel
  relayDest: Channel
  relayStatus: Channel
  relayStatusText: Channel
  relayDist: Channel
  relayAlign: Channel
  relaySpeed: Channel
  relayRepair: Channel
  relayParts: Channel[]
  scanOn: Channel
  scanArc: Channel
  scanPing: Channel
  scanPingOpacity: Channel
  scanBlocked: Channel
  scanBlockedOn: Channel
  statusOn: Channel
  relinkOn: Channel
  relinkHull: Channel
  relinkTime: Channel
  relinkWave: Channel
  promptOn: Channel
  promptKey: Channel
  promptText: Channel
  promptBlocked: Channel
  promptBlockedOn: Channel
  cruiseHint: Channel
  cruiseHintOn: Channel
  bannerOn: Channel
  bannerOpacity: Channel
  bannerName: Channel
  bannerSub: Channel
  toasts: ToastChannels[]
}

/** Builds the view's diffed channels over `el`. `labels` gives the default HUD labels setting (reach-hud.json). */
export function createReachHudView(el: ReachHudElements, labels: ReachHudConfig['labels'] = loadReachHudConfig().labels): ReachHudView {
  return {
    el,
    on: channel(el.root),
    nav: markerChannels(el.nav),
    navRoute: channel(el.nav.root),
    route: markerChannels(el.route),
    labels: el.labels.map((l) => ({
      place: channel(l.root),
      name: channel(l.name),
      dist: channel(l.dist),
      on: channel(l.root),
      kind: channel(l.root),
      text: channel(l.root),
      fade: channel(l.root),
    })),
    declutter: createLabelDeclutter(el.labels.length),
    labelCap: labels.caps[labels.mode],
    frame: { width: 0, height: 0, locked: false },
    far: el.far.map((m) => ({ place: channel(m.root), arrow: channel(m.arrow), dist: channel(m.dist), on: channel(m.root), kind: channel(m.root), edge: channel(m.root) })),
    farLayer: channel(el.farLayer),
    cruisePanel: channel(el.cruise),
    cruiseState: channel(el.cruiseState),
    cruiseCharge: channel(el.cruiseCharge),
    cruiseSpeed: channel(el.cruiseSpeed),
    cruiseEta: channel(el.cruiseEta),
    cruiseEtaOn: channel(el.cruiseEtaWrap),
    cruiseThrottle: channel(el.cruiseThrottle),
    cruiseThrottleFill: channel(el.cruiseThrottleFill),
    cruiseRing: channel(el.cruiseRing),
    escape: markerChannels(el.escape),
    intdOn: channel(el.intd),
    intdEscape: channel(el.intdEscape),
    intdPull: channel(el.intdPull),
    intdTime: channel(el.intdTime),
    relayOn: channel(el.relay),
    relayDest: channel(el.relayDest),
    relayStatus: channel(el.relay),
    relayStatusText: channel(el.relayStatus),
    relayDist: channel(el.relayDist),
    relayAlign: channel(el.relayAlign),
    relaySpeed: channel(el.relaySpeed),
    relayRepair: channel(el.relayRepair),
    relayParts: el.relayParts.map(channel),
    scanOn: channel(el.scan),
    scanArc: channel(el.scanArc),
    scanPing: channel(el.scanPing),
    scanPingOpacity: channel(el.scanPing),
    scanBlocked: channel(el.scanBlocked),
    scanBlockedOn: channel(el.scanBlocked),
    statusOn: channel(el.status),
    relinkOn: channel(el.relink),
    relinkHull: channel(el.relinkHull),
    relinkTime: channel(el.relinkTime),
    relinkWave: channel(el.relinkWave),
    promptOn: channel(el.prompt),
    promptKey: channel(el.promptKey),
    promptText: channel(el.promptText),
    promptBlocked: channel(el.promptBlocked),
    promptBlockedOn: channel(el.promptBlocked),
    cruiseHint: channel(el.cruiseHint),
    cruiseHintOn: channel(el.cruiseHint),
    bannerOn: channel(el.banner),
    bannerOpacity: channel(el.banner),
    bannerName: channel(el.bannerName),
    bannerSub: channel(el.bannerSub),
    toasts: el.toasts.map((t) => ({ text: channel(t), on: channel(t), tone: channel(t), opacity: channel(t), version: -1 })),
  }
}

/** Applies the HUD labels setting (Full / Reduced / Minimal): how many nearby labels keep their text (H2). */
export function setReachHudLabelMode(v: ReachHudView, mode: HudLabelMode, rules: ReachHudRules): void {
  v.labelCap = rules.hud.labels.caps[mode]
}

/** Per frame, before `renderReachHud`: the viewport size (CSS px) and whether the player has a combat target locked. */
export function setReachHudFrame(v: ReachHudView, width: number, height: number, locked: boolean): void {
  const f = v.frame
  f.width = width
  f.height = height
  f.locked = locked
}

// ---- Diffed writers -------------------------------------------------------------------------------------------

function q10(v: number): number {
  return Math.round(v * 10) / 10
}

function translate(ch: Channel, x: number, y: number): void {
  const qx = q10(x)
  const qy = q10(y)
  if (qx === ch.a && qy === ch.b) return
  ch.a = qx
  ch.b = qy
  ch.node.style.transform = `translate3d(${qx}px,${qy}px,0)`
}

function rotate(ch: Channel, deg: number): void {
  const qd = q10(deg)
  if (qd === ch.a) return
  ch.a = qd
  ch.node.style.transform = `rotate(${qd}deg)`
}

function scale(ch: Channel, s: number): void {
  const qs = Math.round(s * 1000) / 1000
  if (qs === ch.a) return
  ch.a = qs
  ch.node.style.transform = `scale(${qs})`
}

function scaleX(ch: Channel, s: number): void {
  const qs = Math.round((s < 0 ? 0 : s > 1 ? 1 : s) * 500) / 500
  if (qs === ch.a) return
  ch.a = qs
  ch.node.style.transform = `scaleX(${qs})`
}

function opacity(ch: Channel, v: number): void {
  const qv = Math.round((v < 0 ? 0 : v > 1 ? 1 : v) * 100) / 100
  if (qv === ch.a) return
  ch.a = qv
  ch.node.style.opacity = String(qv)
}

function dashOffset(ch: Channel, v: number): void {
  const qv = q10(v)
  if (qv === ch.a) return
  ch.a = qv
  ch.node.style.strokeDashoffset = String(qv)
}

/** A text constant or a cached string (compared by value; no string built per frame). */
function text(ch: Channel, s: string): void {
  if (s === ch.s) return
  ch.s = s
  ch.node.textContent = s
}

function distance(ch: Channel, metres: number): void {
  const k = distanceKey(metres)
  if (k === ch.a) return
  ch.a = k
  ch.node.textContent = formatDistance(metres)
}

function navDistance(ch: Channel, metres: number): void {
  const k = navDistanceKey(metres)
  if (k === ch.a) return
  ch.a = k
  ch.node.textContent = formatNavDistance(metres)
}

function speed(ch: Channel, mps: number): void {
  const k = speedKey(mps)
  if (k === ch.a) return
  ch.a = k
  ch.node.textContent = formatSpeed(mps)
}

function duration(ch: Channel, seconds: number): void {
  const k = durationKey(seconds)
  if (k === ch.a) return
  ch.a = k
  ch.node.textContent = formatDuration(seconds)
}

function percent(ch: Channel, v: number): void {
  const n = Math.round(v * 100)
  if (n === ch.a) return
  ch.a = n
  ch.node.textContent = `${n}%`
}

function attr(ch: Channel, name: string, value: string): void {
  if (value === ch.s) return
  ch.s = value
  ch.node.setAttribute(name, value)
}

function flag(ch: Channel, name: string, on: boolean): void {
  attr(ch, name, on ? '1' : '0')
}

// ---- Frame ----------------------------------------------------------------------------------------------------

/** A world marker: on screen at its point with the diamond; off screen at the edge with the rotated arrow. */
function renderMarker(m: MarkerChannels, on: boolean, name: string, metres: number, x: number, y: number, onScreen: boolean, edgeX: number, edgeY: number, angle: number): void {
  flag(m.on, 'data-on', on)
  if (!on) return
  flag(m.edge, 'data-edge', !onScreen)
  if (onScreen) translate(m.place, x, y)
  else {
    translate(m.place, edgeX, edgeY)
    rotate(m.head, angle * RAD_TO_DEG)
  }
  text(m.name, name)
  distance(m.dist, metres)
}

/** Writes `s` into the DOM through the diffed channels. Allocation-free while values are steady. */
export function renderReachHud(v: ReachHudView, s: ReachHudState, rules: ReachHudRules): void {
  flag(v.on, 'data-on', s.on)

  // Nav target and route relay.
  const n = s.nav
  const np = n.point
  renderMarker(v.nav, n.on, n.name, n.distance, np.x, np.y, np.onScreen, np.edgeX, np.edgeY, np.angle)
  if (n.on) flag(v.navRoute, 'data-route', n.route)
  const r = s.route
  const rp = r.point
  renderMarker(v.route, r.on, r.name, r.distance, rp.x, rp.y, rp.onScreen, rp.edgeX, rp.edgeY, rp.angle)

  renderLabels(v, s, rules)

  // Far nav markers: lane rings (ring) and relays (hexagon), dimmer than the nav target (D-249).
  const f = s.far
  flag(v.farLayer, 'data-on', f.on)
  for (let i = 0; i < v.far.length; i++) {
    const m = v.far[i]
    const on = i < f.count
    flag(m.on, 'data-on', on)
    if (!on) continue
    attr(m.kind, 'data-kind', f.kind[i] === FAR_RELAY ? 'relay' : 'laneRing')
    flag(m.edge, 'data-edge', f.edge[i] === 1)
    translate(m.place, f.x[i], f.y[i])
    if (f.edge[i] === 1) rotate(m.arrow, f.angle[i] * RAD_TO_DEG)
    navDistance(m.dist, f.distance[i])
  }

  renderCruise(v, s)

  // Interdiction: the escape-vector marker and the bars.
  const it = s.interdiction
  const ip = it.point
  renderMarker(v.escape, it.on, 'ESCAPE', 0, ip.x, ip.y, ip.onScreen, ip.edgeX, ip.edgeY, ip.angle)
  flag(v.intdOn, 'data-on', it.on)
  if (it.on) {
    scaleX(v.intdEscape, it.escape01)
    scaleX(v.intdPull, it.pull01)
    duration(v.intdTime, it.timeLeftS)
  }

  // Relay hint and repair list.
  const rl = s.relay
  flag(v.relayOn, 'data-on', rl.on)
  if (rl.on) {
    text(v.relayDest, rl.dest)
    attr(v.relayStatus, 'data-status', rl.status)
    text(v.relayStatusText, rl.status === 'online' ? 'ONLINE' : rl.status === 'reboot' ? 'REBOOTING' : 'OFFLINE')
    distance(v.relayDist, rl.distance)
    flag(v.relayAlign, 'data-ok', rl.aligned)
    flag(v.relaySpeed, 'data-ok', rl.slowEnough)
    flag(v.relayRepair, 'data-on', rl.repairOn)
    if (rl.repairOn) {
      for (let i = 0; i < v.relayParts.length && i < rl.delivered.length; i++) partCount(v.relayParts[i], rl.delivered[i], rl.required[i])
    }
  }

  // Deep-scan ring (centre) and its state in the status slot above the cruise panel (H3).
  const sc = s.scan
  flag(v.scanOn, 'data-on', sc.on)
  flag(v.statusOn, 'data-on', sc.on)
  if (sc.on) {
    dashOffset(v.scanArc, SCAN_C * (1 - sc.fill01))
    if (sc.pingOn) {
      scale(v.scanPing, 1 + sc.ping01 * 2.2)
      opacity(v.scanPingOpacity, 1 - sc.ping01)
    } else opacity(v.scanPingOpacity, 0)
    flag(v.scanBlockedOn, 'data-on', sc.blocked.length > 0)
    text(v.scanBlocked, sc.blocked)
  }

  // Relink reboot.
  const rk = s.relink
  flag(v.relinkOn, 'data-on', rk.on)
  if (rk.on) {
    scaleX(v.relinkHull, rk.hull01)
    duration(v.relinkTime, rk.timeLeftS)
    waveText(v.relinkWave, rk.wave, rk.waves)
  }

  // Interaction prompt.
  const p = s.prompt
  flag(v.promptOn, 'data-on', p.on)
  if (p.on) {
    text(v.promptKey, p.key)
    text(v.promptText, p.text)
    flag(v.promptBlockedOn, 'data-on', p.blocked.length > 0)
    text(v.promptBlocked, p.blocked)
  }

  // System banner.
  const b = s.banner
  flag(v.bannerOn, 'data-on', b.on)
  if (b.on) {
    opacity(v.bannerOpacity, b.opacity)
    text(v.bannerName, b.name)
    text(v.bannerSub, b.sub)
  }

  // Toasts.
  const q = s.toasts
  for (let i = 0; i < v.toasts.length; i++) {
    const tc = v.toasts[i]
    const on = i < q.count
    flag(tc.on, 'data-on', on)
    if (!on) continue
    const t = q.slots[i]
    if (t.version !== tc.version) {
      tc.version = t.version
      text(tc.text, t.text)
      attr(tc.tone, 'data-tone', t.tone)
    }
    opacity(tc.opacity, toastOpacity(t, rules.hud))
  }
}

/** The nav and route markers' screen rectangles: their diamond and the name under it outrank every label. */
function reserveMarker(v: ReachHudView, on: boolean, onScreen: boolean, x: number, y: number, name: string, rules: ReachHudRules): void {
  if (!on || !onScreen) return
  const c = rules.hud.labels.declutter
  const w = name.length * c.charPx * 0.5
  const hw = w > c.markerHalfHeightPx ? w : c.markerHalfHeightPx
  reserveLabelRect(v.declutter, x - hw, y - c.markerHalfHeightPx, x + hw, y + c.markerHalfHeightPx + c.markerTextPx)
}

/** Nearby structure labels, decluttered (H2): priority, the setting's cap, the overlap test, reticle suppression, fade. */
function renderLabels(v: ReachHudView, s: ReachHudState, rules: ReachHudRules): void {
  const d = v.declutter
  const f = v.frame
  resetLabelDeclutter(d)
  const np = s.nav.point
  reserveMarker(v, s.nav.on, np.onScreen, np.x, np.y, s.nav.name, rules)
  const rp = s.route.point
  reserveMarker(v, s.route.on, rp.onScreen, rp.x, rp.y, s.route.name, rules)
  const count = s.labelCount < v.labels.length ? s.labelCount : v.labels.length
  declutterLabels(d, count, s.labelX, s.labelY, s.labelDistance, s.labelKind, s.labelText, v.labelCap, f.width * 0.5, f.height * 0.5, f.locked, rules.hud.labels)
  for (let i = 0; i < v.labels.length; i++) {
    const l = v.labels[i]
    const on = i < count && d.show[i] !== LABEL_HIDDEN
    flag(l.on, 'data-on', on)
    if (!on) continue
    attr(l.kind, 'data-kind', s.labelKind[i])
    translate(l.place, s.labelX[i], s.labelY[i])
    opacity(l.fade, d.opacity[i])
    const full = d.show[i] === LABEL_FULL
    flag(l.text, 'data-text', full)
    if (!full) continue
    text(l.name, s.labelText[i])
    distance(l.dist, s.labelDistance[i])
  }
}

function renderCruise(v: ReachHudView, s: ReachHudState): void {
  const c = s.cruise
  attr(v.cruisePanel, 'data-panel', c.panel)
  flag(v.cruiseHintOn, 'data-on', c.hint.length > 0)
  text(v.cruiseHint, c.hint)
  switch (c.panel) {
    case 'hidden':
      return
    case 'ready':
      text(v.cruiseState, 'CRUISE READY')
      return
    case 'charging':
      text(v.cruiseState, 'CHARGING')
      scaleX(v.cruiseCharge, c.charge01)
      return
    case 'cooldown':
      cooldownText(v.cruiseState, c.cooldownS)
      return
    case 'locked':
      text(v.cruiseState, c.lockText)
      return
    case 'interdicted':
      text(v.cruiseState, 'INTERDICTED')
      speed(v.cruiseSpeed, c.speed)
      return
    case 'transit':
      text(v.cruiseState, 'RELAY TRANSIT')
      return
    case 'lane':
      text(v.cruiseState, c.laneText)
      ringText(v.cruiseRing, c.ring, c.rings)
      speed(v.cruiseSpeed, c.speed)
      return
    case 'cruise':
      text(v.cruiseState, 'CRUISE')
      speed(v.cruiseSpeed, c.speed)
      flag(v.cruiseEtaOn, 'data-on', s.nav.on)
      if (s.nav.on) duration(v.cruiseEta, c.etaS)
      percent(v.cruiseThrottle, c.throttle)
      scaleX(v.cruiseThrottleFill, c.throttle)
      return
  }
}

function cooldownText(ch: Channel, seconds: number): void {
  const k = durationKey(seconds)
  if (k === ch.a && ch.s === '') return
  ch.a = k
  ch.s = ''
  ch.node.textContent = `COOLDOWN ${k} s`
}

function ringText(ch: Channel, ring: number, rings: number): void {
  if (ring === ch.a && rings === ch.b) return
  ch.a = ring
  ch.b = rings
  ch.node.textContent = `RING ${ring}/${rings}`
}

function waveText(ch: Channel, wave: number, waves: number): void {
  if (wave === ch.a && waves === ch.b) return
  ch.a = wave
  ch.b = waves
  ch.node.textContent = `WAVE ${wave}/${waves}`
}

function partCount(ch: Channel, delivered: number, required: number): void {
  if (delivered === ch.a && required === ch.b) return
  ch.a = delivered
  ch.b = required
  ch.node.textContent = required < 0 ? `${delivered}/?` : `${delivered}/${required}`
  ch.node.setAttribute('data-done', required >= 0 && delivered >= required ? '1' : '0')
}

// ---- DOM ------------------------------------------------------------------------------------------------------

const MARKER = (cls: string, id: string): string => `
  <div class="rh-marker ${cls}" id="${id}" data-on="0" data-edge="0">
    <svg class="rh-marker-head" viewBox="-16 -16 32 32" aria-hidden="true"><path class="rh-diamond" d="M 0 -9 L 9 0 L 0 9 L -9 0 Z"/><path class="rh-arrow" d="M 0 -13 L 10 6 L 0 1 L -10 6 Z"/></svg>
    <div class="rh-marker-text"><div class="rh-marker-name"></div><div class="rh-marker-dist"></div></div>
  </div>`

/** A far nav marker: a ring (lane ring) or a hexagon (relay), the edge arrow, and the distance under it. */
const FAR_MARKER =
  '<div class="rh-far" data-on="0" data-kind="laneRing" data-edge="0"><svg class="rh-far-shape" viewBox="-8 -8 16 16" aria-hidden="true"><circle class="rh-far-ring" r="4.5"/><path class="rh-far-hex" d="M 0 -5.5 L 4.8 -2.75 L 4.8 2.75 L 0 5.5 L -4.8 2.75 L -4.8 -2.75 Z"/></svg><svg class="rh-far-arrow" viewBox="-12 -12 24 24" aria-hidden="true"><path d="M 0 -11 L 4 -6 L -4 -6 Z"/></svg><span class="rh-far-dist"></span></div>'

function markup(labels: number, toasts: number, rules: ReachHudRules): string {
  let farHtml = ''
  for (let i = 0; i < rules.hud.farMarkers.max; i++) farHtml += FAR_MARKER
  let labelHtml = ''
  for (let i = 0; i < labels; i++) labelHtml += '<div class="rh-label" data-on="0" data-kind="station" data-text="1"><i></i><span class="rh-label-plate"><span class="rh-label-name"></span><span class="rh-label-dist"></span></span></div>'
  let parts = ''
  for (let i = 0; i < PART_IDS.length; i++) parts += `<div class="rh-part"><span>${PART_LABELS[PART_IDS[i]]}</span><b class="rh-part-count" data-done="0">0/?</b></div>`
  let toastHtml = ''
  for (let i = 0; i < toasts; i++) toastHtml += '<div class="rh-toast" data-on="0" data-tone="info"></div>'
  const t = rules.travel.transit
  return `
  <div class="rh-root" data-on="0">
    <div class="rh-layer">
      <div class="rh-far-layer" data-on="0" style="opacity:${rules.hud.farMarkers.opacity}">${farHtml}</div>
      ${labelHtml}
      ${MARKER('rh-route', 'rh-route')}
      ${MARKER('rh-nav', 'rh-nav')}
      ${MARKER('rh-escape', 'rh-escape')}
    </div>
    <div class="rh-scan" data-on="0">
      <svg viewBox="-50 -50 100 100" aria-hidden="true">
        <circle class="rh-scan-track" r="${SCAN_R}"/>
        <circle class="rh-scan-arc" r="${SCAN_R}" transform="rotate(-90)" stroke-dasharray="${SCAN_C.toFixed(2)}" style="stroke-dashoffset:${SCAN_C.toFixed(2)}"/>
        <circle class="rh-scan-ping" r="${SCAN_R}"/>
      </svg>
    </div>
    <div class="rh-banner" data-on="0"><div class="rh-banner-name"></div><div class="rh-banner-sub"></div></div>
    <div class="rh-relink" data-on="0">
      <div class="rh-relink-title">RELAY REBOOT</div>
      <div class="rh-bar rh-relink-bar"><span class="rh-relink-hull"></span></div>
      <div class="rh-relink-row"><span class="rh-relink-time">0 s</span><span class="rh-relink-wave">WAVE 0/0</span></div>
    </div>
    <div class="rh-intd" data-on="0">
      <div class="rh-intd-title">INTERDICTION · HOLD THE ESCAPE VECTOR</div>
      <div class="rh-intd-row"><span>ESCAPE</span><div class="rh-bar rh-intd-escape-bar"><span class="rh-intd-escape"></span></div></div>
      <div class="rh-intd-row"><span>PULL</span><div class="rh-bar rh-intd-pull-bar"><span class="rh-intd-pull"></span></div></div>
      <div class="rh-intd-time">0 s</div>
    </div>
    <div class="rh-relay" data-on="0" data-status="online">
      <div class="rh-relay-title">RELAY ▸ <b class="rh-relay-dest"></b></div>
      <div class="rh-relay-row"><span class="rh-relay-status">ONLINE</span><span class="rh-relay-dist"></span></div>
      <div class="rh-check rh-relay-align" data-ok="0">ALIGN ≤ ${t.alignDeg}°</div>
      <div class="rh-check rh-relay-speed" data-ok="0">SPEED &lt; ${t.maxSpeed} m/s</div>
      <div class="rh-relay-repair" data-on="0"><div class="rh-relay-sub">REPAIR LIST · DELIVERED/REQUIRED</div>${parts}</div>
    </div>
    <div class="rh-prompt" data-on="0">
      <span class="rh-prompt-key"></span><span class="rh-prompt-text"></span>
      <div class="rh-prompt-blocked" data-on="0"></div>
    </div>
    <div class="rh-cruise" data-panel="hidden">
      <div class="rh-status" data-on="0"><span class="rh-scan-label">DEEP SCAN</span><span class="rh-scan-blocked" data-on="0"></span></div>
      <div class="rh-cruise-state"></div>
      <div class="rh-cruise-hint" data-on="0"></div>
      <div class="rh-bar rh-cruise-charge-bar"><span class="rh-cruise-charge"></span></div>
      <div class="rh-cruise-row">
        <span class="rh-cruise-speed"></span>
        <span class="rh-cruise-eta-wrap" data-on="0">ETA <b class="rh-cruise-eta">--</b></span>
        <span class="rh-cruise-ring"></span>
      </div>
      <div class="rh-cruise-throttle-row"><span>THR</span><div class="rh-bar"><span class="rh-cruise-throttle-fill"></span></div><b class="rh-cruise-throttle">100%</b></div>
    </div>
    <div class="rh-toasts">${toastHtml}</div>
  </div>`
}

/** Builds the reach HUD under a new `#reach-hud` in `parent` and returns the elements the view writes. Init only. */
export function mountReachHud(parent: HTMLElement, rules: ReachHudRules): { host: HTMLElement; el: ReachHudElements } {
  const host = parent.ownerDocument.createElement('div')
  host.id = 'reach-hud'
  host.innerHTML = markup(rules.hud.labels.max, rules.hud.toasts.max, rules)
  parent.appendChild(host)
  const q = (sel: string, from: ParentNode = host): HTMLElement => {
    const el = from.querySelector<HTMLElement>(sel)
    if (!el) throw new Error(`Reach HUD element ${sel} missing`)
    return el
  }
  const all = (sel: string): HTMLElement[] => Array.from(host.querySelectorAll<HTMLElement>(sel))
  const markerNodes = (id: string): MarkerNodes => {
    const root = q(`#${id}`)
    return { root, head: q('.rh-marker-head', root), name: q('.rh-marker-name', root), dist: q('.rh-marker-dist', root) }
  }
  return {
    host,
    el: {
      root: q('.rh-root'),
      nav: markerNodes('rh-nav'),
      route: markerNodes('rh-route'),
      labels: all('.rh-label').map((root) => ({ root, name: q('.rh-label-name', root), dist: q('.rh-label-dist', root) })),
      far: all('.rh-far').map((root) => ({ root, arrow: q('.rh-far-arrow', root), dist: q('.rh-far-dist', root) })),
      farLayer: q('.rh-far-layer'),
      cruise: q('.rh-cruise'),
      cruiseState: q('.rh-cruise-state'),
      cruiseCharge: q('.rh-cruise-charge'),
      cruiseSpeed: q('.rh-cruise-speed'),
      cruiseEta: q('.rh-cruise-eta'),
      cruiseEtaWrap: q('.rh-cruise-eta-wrap'),
      cruiseThrottle: q('.rh-cruise-throttle'),
      cruiseThrottleFill: q('.rh-cruise-throttle-fill'),
      cruiseRing: q('.rh-cruise-ring'),
      escape: markerNodes('rh-escape'),
      intd: q('.rh-intd'),
      intdEscape: q('.rh-intd-escape'),
      intdPull: q('.rh-intd-pull'),
      intdTime: q('.rh-intd-time'),
      relay: q('.rh-relay'),
      relayDest: q('.rh-relay-dest'),
      relayStatus: q('.rh-relay-status'),
      relayDist: q('.rh-relay-dist'),
      relayAlign: q('.rh-relay-align'),
      relaySpeed: q('.rh-relay-speed'),
      relayRepair: q('.rh-relay-repair'),
      relayParts: all('.rh-part-count'),
      scan: q('.rh-scan'),
      scanArc: q('.rh-scan-arc'),
      scanPing: q('.rh-scan-ping'),
      scanBlocked: q('.rh-scan-blocked'),
      status: q('.rh-status'),
      relink: q('.rh-relink'),
      relinkHull: q('.rh-relink-hull'),
      relinkTime: q('.rh-relink-time'),
      relinkWave: q('.rh-relink-wave'),
      prompt: q('.rh-prompt'),
      promptKey: q('.rh-prompt-key'),
      promptText: q('.rh-prompt-text'),
      promptBlocked: q('.rh-prompt-blocked'),
      cruiseHint: q('.rh-cruise-hint'),
      banner: q('.rh-banner'),
      bannerName: q('.rh-banner-name'),
      bannerSub: q('.rh-banner-sub'),
      toasts: all('.rh-toast'),
    },
  }
}
